import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Star } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernTestimonials — the «Moderno» theme Testimonials (s139, approved by
// Anthony: "Quote Wall"). White cards on a stone band: serif quote, then the
// person (photo, name, role); the middle card sits lower to break the grid.
// More than 3 → pages of 3 with arrows. Stars only when a testimonial has a
// rating (no invented 5-star score). Header = CMS → Section Manager →
// Testimonials — Section Configuration. Colours come only from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);
const PER = 3;

export function TestimonialCard({ t, offset }) {
  const tt = useT();
  const photo = img(t.avatar || t.image);
  const role = tt(t.role) || tt(t.title) || tt(t.company);
  const rating = Number(t.rating) || 0;
  return (
    <figure className={`bg-white p-7 md:p-8 flex flex-col gap-6 ${offset ? 'md:mt-12' : ''}`} style={{ border: '1px solid rgba(20,26,58,.08)' }} data-testid={`testimonial-${t.id}`}>
      <span aria-hidden="true" className="leading-none" style={{ fontFamily: SERIF, fontSize: 56, height: 28, color: 'var(--color-accent, #ff9c1b)' }}>“</span>
      <blockquote className="m-0" style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(1.2rem, 1.6vw, 1.45rem)', lineHeight: 1.35, color: 'var(--color-heading, #1a2332)' }}>
        {tt(t.content)}
      </blockquote>
      {rating > 0 && (
        <div className="flex gap-0.5" aria-label={`${rating} out of 5`}>
          {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-3.5 h-3.5" style={{ color: 'var(--color-accent, #ff9c1b)', fill: s <= rating ? 'var(--color-accent, #ff9c1b)' : 'none' }} />)}
        </div>
      )}
      <figcaption className="flex items-center gap-3 mt-auto">
        {photo && <img src={photo} alt="" className="w-12 h-12 rounded-full object-cover flex-none" />}
        <div>
          <div className="font-semibold text-sm" style={{ color: 'var(--color-heading, #1a2332)' }}>{tt(t.name)}</div>
          {role && <div className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.08em', color: 'var(--accent-strong, #b86500)' }}>{role}</div>}
        </div>
      </figcaption>
    </figure>
  );
}

export default function ModernTestimonials({ items, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const list = (items || []).filter(t => t.visible !== false && itemHasLocale(t.content, lang));
  const [page, setPage] = useState(0);
  if (!list.length) return null;
  const pages = Math.ceil(list.length / PER);
  const cur = Math.min(page, pages - 1);
  const shown = list.slice(cur * PER, cur * PER + PER);

  const eyebrow = tt(cmsConfig.eyebrow);
  const title = tt(cmsConfig.title) || 'What People Say';
  const subtitle = tt(cmsConfig.subtitle);
  const ctaText = tt(cmsConfig.cta_text);
  const ctaUrl = tt(cmsConfig.cta_url);
  const words = title.trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;

  const arrow = 'w-11 h-11 rounded-full grid place-items-center border transition-transform duration-150 active:scale-95 disabled:opacity-30';

  return (
    <section id="testimonials" data-testid="testimonials-section" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 5%, #f6f5f2)' }}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 lg:mb-16">
          <div className="flex flex-col gap-4 max-w-2xl">
            {eyebrow && (
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
                <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{eyebrow}
              </div>
            )}
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }} data-testid="testimonials-title">
              {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
            </h2>
            {subtitle && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{subtitle}</p>}
          </div>
          <div className="flex items-center gap-5">
            {ctaText && ctaUrl && (
              <a href={ctaUrl} target={cmsConfig.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="mx-link inline-flex items-center gap-2 font-bold text-sm" style={{ color: 'var(--accent-strong, #b86500)' }} data-testid="testimonials-cta">
                {ctaText} <ArrowRight className="w-4 h-4" />
              </a>
            )}
            {pages > 1 && (
              <div className="flex gap-2" data-testid="testimonials-carousel-arrows">
                <button type="button" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={cur === 0} aria-label="Previous" className={arrow} style={{ borderColor: 'rgba(20,26,58,.18)', color: 'var(--color-heading, #1a2332)' }}><ArrowLeft className="w-4 h-4" /></button>
                <button type="button" onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={cur >= pages - 1} aria-label="Next" className={arrow} style={{ borderColor: 'var(--color-accent, #ff9c1b)', background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)' }}><ArrowRight className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 items-start">
          {shown.map((t, i) => <TestimonialCard key={t.id} t={t} offset={i === 1} />)}
        </div>
      </div>
    </section>
  );
}
